import { CurrencyValue as TCurrencyValue } from '@payaca/types/currencyTypes';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();
const getCurrencyFormatter = (
  locale: string,
  currencyCode: string,
  fractionDigits: number
) => {
  const key = `${locale}:${currencyCode}:${fractionDigits}`;
  let formatter = currencyFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    currencyFormatterCache.set(key, formatter);
  }
  return formatter;
};

export function formatCompactProjectValue(
  value: TCurrencyValue,
  locale: string
): string {
  const major = value.value / Math.pow(10, value.currency.exponent);
  const fmt = (n: number, fractionDigits: number) =>
    getCurrencyFormatter(locale, value.currency.code, fractionDigits).format(n);

  if (major < 1000) return fmt(major, 0);
  if (major < 10000) return `${fmt(major / 1000, 1)}k`;
  return `${fmt(major / 1000, 0)}k`;
}

export function formatShortDuration(earlier: Date, later: Date): string {
  const minutes = differenceInMinutes(later, earlier);
  if (minutes < 1) return '< 1min';
  if (minutes < 60) return `${minutes}min`;
  const hours = differenceInHours(later, earlier);
  if (hours < 24) return `${hours}h`;
  const days = differenceInDays(later, earlier);
  if (days < 30) return `${days}d`;
  // differenceInMonths is calendar-month based, so a 30-day span that doesn't
  // cross a month boundary returns 0. Fall back to days in that case.
  const months = differenceInMonths(later, earlier);
  if (months < 1) return `${days}d`;
  if (months < 12) return `${months}mo`;
  const years = differenceInYears(later, earlier);
  if (years < 1) return `${months}mo`;
  return `${years}y`;
}
